(() => {
  if (customElements.get('oilogic-bv-reviews')) return;

/*
 * <oilogic-bv-reviews> — regroups the Bazaarvoice reviews summary.
 *
 * Bazaarvoice renders the reviews widget across two hosted-display roots:
 *   #BVRRSearchContainer > .bv-masthead  → .bv-secondary-summary (stars + % recommend)
 *   #BVRRContainer > .bv-header          → .bv-section-summary (rating snapshot)
 *                                          .bv-action-bar (heading + write-review CTA)
 *
 * The design groups all three inside .bv-section-summary, which cannot be done
 * with CSS because .bv-secondary-summary lives in a different render root. This
 * element watches for BV hydration and moves .bv-secondary-summary and
 * .bv-action-bar into .bv-section-summary. The observer stays connected so the
 * move re-applies whenever BV re-renders (filtering, sorting, pagination), and
 * a stale copy left behind by a partial re-render is replaced, never duplicated.
 *
 * BV click handlers are delegated at its container level, so moved nodes keep
 * working (verified against the write-review CTA).
 */
class OilogicBvReviews extends HTMLElement {
  static #clickDropdownsInstalled = false;
  #observer = null;
  #applyTimer = 0;

  connectedCallback() {
    OilogicBvReviews.#installClickDropdowns();
    this.#observer = new MutationObserver(() => this.#schedule());
    // Body-level: the BV app block's duplicate display (see
    // #adoptSharedWidget) hydrates outside this element.
    this.#observer.observe(document.body, { childList: true, subtree: true });
    this.#schedule();
  }

  // BV opens its dropdown flyouts on hover. Swallow the real (isTrusted)
  // hover events on the dropdowns and their body-appended flyout
  // (.bv-absolute-top-container) so only BV's click handler — which toggles —
  // remains: the pills become plain click buttons. With hover gone BV has no
  // close path besides re-clicking the same pill, so outside-click and Escape
  // re-click the pill that opened the current flyout. Document-level and
  // installed once: the flyout lives outside this element.
  static #installClickDropdowns() {
    if (OilogicBvReviews.#clickDropdownsInstalled) return;
    OilogicBvReviews.#clickDropdownsInstalled = true;

    const isDropdownUi = (target) =>
      target instanceof Element && !!target.closest('.bv-dropdown, .bv-absolute-top-container');

    for (const type of ['mouseover', 'mouseout', 'mouseenter', 'mouseleave']) {
      document.addEventListener(
        type,
        (event) => {
          if (event.isTrusted && isDropdownUi(event.target)) event.stopPropagation();
        },
        true
      );
    }

    let openButton = null;
    const closeOpenFlyout = () => {
      // Guard on BV's own open marker so a stale reference never re-opens.
      if (openButton && document.querySelector('.bv-absolute-top-container.bv-dropdown-in')) {
        openButton.click();
      }
      openButton = null;
    };

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('.bv-dropdown-target .bv-focusable');
      if (button) {
        openButton = button;
        return;
      }
      if (!isDropdownUi(event.target)) closeOpenFlyout();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeOpenFlyout();
    });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    clearTimeout(this.#applyTimer);
  }

  // BV hydrates in bursts of many writes, and moving its nodes mid-pipeline
  // can silently abort its whole render (empty #BVRRContainer, intermittent —
  // depends on when a pass lands relative to the burst). Wait for the subtree
  // to stay quiet for a beat so BV always finishes first; our own writes
  // re-arm the timer and the follow-up pass settles as a no-op.
  #schedule() {
    clearTimeout(this.#applyTimer);
    this.#applyTimer = setTimeout(() => {
      this.#adoptSharedWidget();
      this.#merge();
      this.#relocateFilters();
      this.#forceFilterOpen();
    }, 250);
  }

  // The Bazaarvoice app block on the product template renders a duplicate
  // reviews display (BV marks its cores .bv-shared) and BV hydrates only one
  // of the two registered containers — sometimes the app block's, leaving
  // this section's mount empty. Adopt the hydrated display into our mount so
  // the section always shows; section-product-reviews.css hides .bv-shared
  // only while it is outside .product-reviews. The donor's root containers
  // (#BVRR*Container) move wholesale: BV scopes ALL its CSS to the
  // .bv-cleanslate wrapper inside them, and its handlers are bound within
  // the moved subtree — both keep working. Our own never-hydrated roots are
  // dropped first (the #BVRR* ids exist in both copies).
  #adoptSharedWidget() {
    const mount = this.querySelector('[data-bv-show="reviews"]');
    if (!mount || mount.querySelector('.bv-core-container-53')) return;

    const donor = [...document.querySelectorAll('[data-bv-show="reviews"]')]
      .find((el) => !this.contains(el) && el.querySelector('.bv-header, .bv-content-item'));
    if (!donor) return;

    [...mount.children].forEach((el) => el.remove());
    [...donor.children].forEach((el) => mount.appendChild(el));
  }

  #merge() {
    const target = this.querySelector('.bv-section-summary');
    if (!target) return;

    // BV hydrates its two roots at different times, so arrival order is not
    // deterministic. Adopt whatever exists, then normalize the tail order:
    // rating snapshot table (BV's own child) → secondary summary → action bar.
    const parts = ['bv-secondary-summary', 'bv-action-bar']
      .map((name) => this.#adopt(target, name))
      .filter(Boolean);
    if (!parts.length) return;

    const tail = [...target.children].slice(-parts.length);
    if (parts.every((el, i) => tail[i] === el)) return;

    parts.forEach((el) => target.appendChild(el));
    target.setAttribute('data-bv-merged', '');
    this.dispatchEvent(new CustomEvent('oilogic:bv:summary-merged', { bubbles: true }));
  }

  // The design rearranges BV's filter UI, re-applied after every BV
  // re-render (same stale-copy handling as #merge via #adopt):
  // - the whole rating filter panel (.bv-content-filter, with
  //   .bv-filter-controls/.bv-filters kept nested inside it) goes in the
  //   offset control bar (.bv-control-bar.bv-control-bar-filter-offset);
  // - the active-filter chips (.bv-active-filters) go at the bottom of the
  //   reviews header (.bv-header). Order matters: the panel moves first so
  //   the chips are pulled out of the freshly adopted panel.
  #relocateFilters() {
    this.#moveInto(this.querySelector('.bv-control-bar-filter-offset'), 'bv-content-filter');
    this.#moveInto(this.querySelector('.bv-header'), 'bv-active-filters');
  }

  // Adopt the freshest .{name} into container as its last child. No-op when
  // it is already there or when the move would nest a node inside itself.
  #moveInto(container, name) {
    if (!container) return;

    const el = this.#adopt(container, name);
    if (!el || el.parentElement === container || el.contains(container)) return;
    container.appendChild(el);
  }

  // The design keeps the rating filter permanently expanded: apply BV's own
  // expanded-state classes so the DOM matches what its "Filter Reviews"
  // toggle would produce (the button itself is hidden via CSS). Class writes
  // don't re-trigger the observer (it only watches childList), and every
  // hydration/re-render pass reasserts the state. .bv-filters is queried from
  // the element root because #relocateFilters moves it out of the panel.
  #forceFilterOpen() {
    const panel = this.querySelector('.bv-content-filter');
    if (!panel) return;

    panel.classList.remove('bv-filter-controls-hidden');
    panel.classList.add('bv-filter-controls-visible');
    this.querySelectorAll('.bv-filter-controls, .bv-filters').forEach((el) => {
      el.classList.remove('bv-hidden');
      // BV collapses via inline style on some re-renders — clear it too.
      if (el.style.display === 'none') el.style.removeProperty('display');
    });

    const button = this.querySelector('.bv-expand-filter-button');
    button?.classList.add('bv-filter-active');
    button?.setAttribute('aria-expanded', 'true');
  }

  // Resolve the freshest .{name} for target, dropping a stale copy left in
  // target by a previous pass so BV re-renders never leave duplicates. The
  // returned node is (re)attached by #merge's ordering pass.
  #adopt(target, name) {
    const candidates = [...this.querySelectorAll(`.${name}`)];
    const fresh = candidates.find((el) => el.parentElement !== target);
    const current = candidates.find((el) => el.parentElement === target);
    if (!fresh) return current ?? null;

    current?.remove();
    return fresh;
  }
}

customElements.define('oilogic-bv-reviews', OilogicBvReviews);
})();
